"use client";

import { Button, Heading, ProductCard } from "@/components/atoms";
import SkeletonPC from "@/components/atoms/ProductCard/SkeletonPC";
import ProductGrid from "@/components/molecules/ProductGrid";

import useIntersection from "@/lib/hooks/useIntersection";
import useScrollToTop from "@/lib/hooks/useScrollToTop";
import type {
  ContentBlock,
  GetListingResponse,
} from "@depict-ai/types/api/GetListingResponse";
import { useMemo } from "react";

import { HlsPlayer } from "@/components/atoms/HLSPlayer";
import { ProductCard as PC } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { ProductListingPageStoryblok } from "@/types/storyblok-blok-types";
import Image from "next/image";
import Link from "next/link";
import styles from "./products-section.module.css";

type GridItem =
  | { type: "product"; product: PC; index: number }
  | {
      type: "content-block";
      block: ContentBlock;
      index: number;
      mobileRow: number; // Calculated mobile row position
      mobileOnly: boolean; // Hidden on desktop (beyond desktop rows)
    };

/**
 * Merges products and content blocks into a single list for rendering.
 *
 * Depict's `row` value is based on DESKTOP layout (4 products per row).
 * We calculate insertion positions using desktop rows to maintain
 * correct product-relative positioning on both layouts.
 *
 * Desktop uses explicit CSS grid-row positioning.
 * Mobile uses DOM order with auto-flow.
 */
function mergeProductsAndContentBlocks(
  products: PC[],
  contentBlocks: ContentBlock[],
): GridItem[] {
  if (contentBlocks.length === 0) {
    return products.map((product, index) => ({
      type: "product",
      product,
      index,
    }));
  }

  const DESKTOP_PRODUCTS_PER_ROW = 4;

  // Calculate max rows (Depict rows are 0-indexed, based on desktop layout)
  // Desktop: 4 products/row, Mobile: 2 products/row (so 2x the rows)
  const maxDesktopRow = Math.ceil(products.length / DESKTOP_PRODUCTS_PER_ROW);
  const maxMobileRow = maxDesktopRow * 2; // Mobile has ~2x the rows for same products

  // Filter: include blocks that fit on mobile (more permissive)
  // Blocks beyond desktop rows will be marked as mobileOnly
  const validBlocks = contentBlocks
    .filter((block) => block.row <= maxMobileRow)
    .sort((a, b) => a.row - b.row);

  // Calculate mobile row for each content block
  // On desktop, blocks with span_columns < 4 share rows with products
  // On mobile, blocks with span_columns >= 2 are full-width (exclusive rows)
  // So blocks with 2 <= span_columns < 4 add extra rows on mobile
  const sortedBlocks = [...validBlocks].sort((a, b) => a.row - b.row);
  const blockMobileRows = new Map<ContentBlock, number>();

  let mobileRowOffset = 0; // Extra rows from earlier full-width-on-mobile blocks

  for (const block of sortedBlocks) {
    // Desktop row (CSS 1-indexed)
    const desktopRow = block.row + 1;
    // Mobile row = desktop row + offset from earlier blocks that are full-width on mobile
    const mobileRow = desktopRow + mobileRowOffset;
    blockMobileRows.set(block, mobileRow);

    // If this block is full-width on mobile (span_columns >= 2) but shares on desktop,
    // it adds (span_rows - 1) extra rows for subsequent blocks
    // (The block itself takes span_rows, but on desktop it would share 1 row with products)
    const isFullWidthOnMobile = block.span_columns >= 2;
    const isFullWidthOnDesktop = block.span_columns === 4;
    if (isFullWidthOnMobile && !isFullWidthOnDesktop) {
      // This block takes exclusive rows on mobile but shares on desktop
      // Add (span_rows - 1) because the first row "replaces" its desktop position
      mobileRowOffset += block.span_rows - 1;
    } else if (isFullWidthOnMobile && isFullWidthOnDesktop) {
      // Full-width on both - no extra offset needed, same behavior
    }
  }

  // Create insertion map: product index -> content blocks
  const insertionMap = new Map<number, ContentBlock[]>();

  for (const block of validBlocks) {
    // Convert Depict row to product index
    const insertIndex = block.row * DESKTOP_PRODUCTS_PER_ROW;
    const clampedIndex = Math.min(insertIndex, products.length);

    if (!insertionMap.has(clampedIndex)) {
      insertionMap.set(clampedIndex, []);
    }
    insertionMap.get(clampedIndex)!.push(block);
  }

  // Build the merged list
  const items: GridItem[] = [];
  let blockIndex = 0;

  for (let i = 0; i <= products.length; i++) {
    const blocksAtPosition = insertionMap.get(i);
    if (blocksAtPosition) {
      // Sort blocks: left first, then right
      blocksAtPosition.sort((a, b) => {
        if (a.position === "left" && b.position === "right") return -1;
        if (a.position === "right" && b.position === "left") return 1;
        return 0;
      });

      for (const block of blocksAtPosition) {
        items.push({
          type: "content-block",
          block,
          index: blockIndex++,
          mobileRow: blockMobileRows.get(block) ?? 1,
          mobileOnly: block.row > maxDesktopRow, // Beyond desktop rows = mobile only
        });
      }
    }

    if (i < products.length) {
      items.push({ type: "product", product: products[i], index: i });
    }
  }

  return items;
}

const ProductsSection = ({
  data: { plpContent, listingPage, fetchNextPage, hasNextPage },
}: {
  data: {
    plpContent: (
      | {
          products: PC[];
          editorial?: NonNullable<ProductListingPageStoryblok["bloks"]>[number];
        }
      | undefined
    )[];
    listingPage: GetListingResponse | null;
    fetchNextPage: () => Promise<void>;

    hasNextPage: boolean;
  };
}) => {
  const scrollToTop = useScrollToTop();

  const callbackFunction = (
    entries: IntersectionObserverEntry[],
    _observer: IntersectionObserver,
  ) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage) {
      fetchNextPage();
    }
  };
  const { Ref } = useIntersection(callbackFunction);

  // Flatten all products from all sections
  const allProducts = useMemo(() => {
    return plpContent?.flatMap((section) => section?.products ?? []) ?? [];
  }, [plpContent]);

  // Merge products and content blocks into correct order
  // This is screen-size agnostic - CSS Grid handles responsive layout
  const gridItems = useMemo(() => {
    return mergeProductsAndContentBlocks(
      allProducts,
      listingPage?.content_blocks ?? [],
    );
  }, [allProducts, listingPage?.content_blocks]);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "6rem" }}>
      <ProductGrid id="grid">
        {gridItems.map((item) => {
          if (item.type === "product") {
            return (
              <ProductCard
                className="product-card"
                key={`product-${item.product?.core?.slug}-${item.index}`}
                product={item.product}
              />
            );
          }
          if (item.type === "content-block") {
            return (
              <ContentBlockItem
                block={item.block}
                key={`content-block-${item.index}`}
                mobileRow={item.mobileRow}
                mobileOnly={item.mobileOnly}
              />
            );
          }
          return null;
        })}
        {hasNextPage && (
          <>
            <div id="observer" ref={Ref}>
              <SkeletonPC />
            </div>
            <SkeletonPC /> <SkeletonPC /> <SkeletonPC />
          </>
        )}
      </ProductGrid>

      {!hasNextPage && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            gap: "1.5rem",
          }}
        >
          <Heading as="span" styledAs="h4">
            There are no more products to show
          </Heading>
          <Button outlined onClick={scrollToTop}>
            Back to top
          </Button>
        </div>
      )}
    </section>
  );
};

function getDepictColumn(block: ContentBlock) {
  if (block.span_columns === 4) {
    return `1 / span 4`;
  }
  switch (block.position) {
    case "left":
      return `1 / span ${block.span_columns}`;
    case "right":
      return `span ${block.span_columns} / -1`;
    default:
      return `1 / span ${block.span_columns}`;
  }
}

function getDepictLink(block: ContentBlock) {
  switch (typeof block.content.link) {
    case "string":
      return block.content.link;
    default:
      return undefined;
  }
}

/**
 * Gets the CSS grid-column value for mobile layout.
 * On mobile: span_columns >= 2 -> full width, span_columns 1 -> half width with position
 */
function getMobileColumnSpan(block: ContentBlock): string {
  if (block.span_columns >= 2) {
    return "1 / -1"; // Full width
  }
  // Half width - respect left/right position
  // Mobile grid is 4 columns, products span 2, so content blocks span 2 as well
  if (block.position === "right") {
    return "3 / span 2"; // Right half
  }
  return "1 / span 2"; // Left half (default)
}

const ContentBlockItem = ({
  block,
  mobileRow,
  mobileOnly,
}: {
  block: ContentBlock;
  mobileRow: number;
  mobileOnly: boolean;
}) => {
  const link = getDepictLink(block);
  const Component = link ? Link : "article";

  // CSS handles responsive behavior via media queries and CSS variables
  const desktopColumn = getDepictColumn(block);
  const mobileColumn = getMobileColumnSpan(block);
  const isFullWidth = block.span_columns >= 2;

  // Desktop: explicit row from Depict (CSS Grid is 1-indexed, Depict is 0-indexed)
  // Mobile: calculated row that accounts for earlier content blocks
  const desktopRowCss = `${block.row + 1} / span ${block.span_rows}`;
  const mobileRowCss = `${mobileRow} / span ${block.span_rows}`;

  return (
    <Component
      href={link!}
      className={`${styles.contentBlock} ${mobileOnly ? styles.mobileOnly : ""}`}
      data-span-all={block.span_columns === 4 ? true : undefined}
      data-full-width={isFullWidth ? true : undefined}
      style={
        {
          position: "relative",
          "--col-span": block.span_columns,
          "--row-span": block.span_rows,
          "--grid-column-desktop": desktopColumn,
          "--grid-column-mobile": mobileColumn,
          "--grid-row-desktop": desktopRowCss,
          "--grid-row-mobile": mobileRowCss,
        } as React.CSSProperties
      }
    >
      <div className={styles.contentBlockWrap}>
        <ContentBlockMedia block={block} />
      </div>
    </Component>
  );
};

const ContentBlockMedia = ({ block }: { block: ContentBlock }) => {
  if (block.content.type === "video") {
    return (
      <HlsPlayer
        loop
        className={styles.video}
        autoPlay
        muted
        src={block.content.url}
      />
    );
  }
  return (
    <Image
      priority={block.row < 4}
      fill
      style={{
        backgroundColor: "var(--color-primary-10)",
        objectFit: "cover",
      }}
      src={block.content.url}
      alt={block.content.url}
    />
  );
};

export default ProductsSection;
